import classes from 'classnames';
import ReactSlider, { ReactSliderProps } from 'react-slider';
import { twMerge } from 'tailwind-merge';
import HandleIcon from 'assets/icons/vertical-hamburger.svg?react';
import { numberToLocale } from 'utils/translate';
import styles from './Slider.module.scss';

export type Props = {
  min: number;
  max: number;
  value?: number;
  heading?: string;
  step?: number;
  units?: string;
  onChange?: ReactSliderProps['onChange'];
  onAfterChange: ReactSliderProps['onAfterChange'];
  dataTest?: string;
  unitsTransform?: (value: number) => string;
  size?: 'default' | 'compact';
  disabled?: boolean;
};

export default function Slider({
  min,
  max,
  value,
  heading,
  step,
  units,
  onChange,
  onAfterChange,
  dataTest = 'slider',
  unitsTransform,
  disabled = false,
}: Props) {
  const leftMargin = min + (max - min) * 0.1;
  const rightMargin = max - (max - min) * 0.1;

  const renderThumb: ReactSliderProps['renderThumb'] = (props, state) => {
    const { valueNow, index } = state;
    const unitsStr = unitsTransform ? unitsTransform(valueNow) : units;

    return (
      <div key={index}>
        {
          <div {...props} key="slider-key">
            {/* // Thumb */}
            <div
              className="flex justify-center items-center bg-background rounded-full cursor-grab shadow-[0px_-1px_1px_0px_rgba(0,0,0,0.11)_inset,0px_1px_4px_0px_rgba(0,0,0,0.44)] absolute -translate-x-[18px] h-9 w-9 -bottom-7"
              data-test={`${dataTest}-handler`}
            >
              <HandleIcon className="h-4 w-4 fill-primary" />

              {/* Tooltip on top of the handle */}
              <div
                className={classes(
                  twMerge(
                    'transition-opacity absolute rounded-lg bg-background shadow-[0px_2px_16px_0px_#00000031] after:absolute after:h-3 after:w-3  after:bg-background after:-bottom-[5px] after:left-[calc(50%-6px)] after:rotate-45 bottom-12',
                    valueNow > rightMargin &&
                      'right-0 after:left-[calc(100%-22px)]',
                    valueNow < leftMargin && 'left-0 after:left-3',
                  ),
                )}
              >
                <div className="flex justify-center items-baseline min-w-14 px-2 pb-2 gap-1">
                  <span data-test="thumb" className="text-2xl font-bold">
                    {numberToLocale(valueNow)}
                  </span>
                  {units && (
                    <span className="text-sm font-semibold" data-test="unit">
                      {unitsStr}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    );
  };

  return (
    <div data-test={dataTest} className="group">
      {heading && (
        <div
          className="text-lg font-semibold pb-12 sn: pt-3"
          data-test={`${dataTest}-header`}
        >
          {heading}
        </div>
      )}
      <ReactSlider
        className="p-5"
        min={min}
        max={max}
        step={step}
        value={value}
        trackClassName={styles.track}
        renderThumb={renderThumb}
        onChange={onChange}
        onAfterChange={onAfterChange}
        disabled={disabled}
      />
      <div className="mt-1 text-muted text-xs flex justify-between">
        <span data-test={`${dataTest}-min`}>
          {min} {unitsTransform ? unitsTransform(min) : units}
        </span>
        <span data-test={`${dataTest}-max`}>
          {max} {unitsTransform ? unitsTransform(max) : units}
        </span>
      </div>
    </div>
  );
}
